<template>
  <div class="container-full inset-0" :class="IsModal ? 'fixed bg-black bg-opacity-40 z-[60]' : 'pt-10'" @click.self="closeModal" tabindex="-1" ref="loginContainer" @keydown="trapFocus">
    <Spinner :is-loading="isLoading" />

    <div class="max-w-[660px] w-full mx-auto" :class="IsModal ? 'absolute -translate-x-1/2 left-1/2 top-1/2 -translate-y-1/2' : ''">
      <button @click="closeModal" class="absolute right-6 top-6 z-50 card-wrapper" :class="IsModal ? '' : '!hidden'" role="button" aria-label="Close modal" tabindex="0"><SvgHelper name="x" /></button>
      <FormKit v-model="formData" :actions="false" type="form" @submit="handleSubmit" id="login-modal" role="dialog" aria-modal="true">
        <section>
          <div class="bg-dania-red-10 relative lg:px-11 lg:w-auto px-4 lg:h-[588px] h-[calc(100svh-106px)] flex flex-col justify-center">
            <template v-if="showDefault && !isLoggedIn">
              <h2 class="lg:text-h1 text-h1-sm text-center mb-6">{{ dictionary['Login.LogIn'] }}</h2>
              <span class="lg:text-t3-light text-t3-light text-center mb-3 flex justify-center">{{ dictionary['Login.LogInOnYourProfileAtRealdania'] }}</span>
              <span class="lg:text-p2 text-p2 text-center flex">{{ dictionary['Login.RemarkYouCannotLogInToRealdaniaByAndBygClubWithYourRealdaniaLoginOrIfYouAreNotAClubMember'] }}</span>
              <div class="flex flex-col gap-6 mt-7">
                <FormKit
                  :validation-messages="{
                    required: 'E-mail er påkrævet',
                  }"
                  name="username"
                  :label="`${dictionary['Login.Email']}`"
                  type="text"
                  validation="required"
                  placeholder="Indtast din e-mail"
                  input-class="text-dania-grey-600"
                  aria-required="true"
                  autocomplete="username"
                />
                <div class="relative">
                  <FormKit
                    name="password"
                    :validation-messages="{
                      required: 'Adgangskode er påkrævet',
                    }"
                    :label="`${dictionary['Login.Password']}`"
                    :type="showPassword ? 'text' : 'password'"
                    placeholder="Indtast din adgangskode"
                    input-class="text-dania-grey-600"
                    aria-required="true"
                    autocomplete="current-password"
                  />
                  <button type="button" @click="togglePasswordVisibility" class="absolute right-[16px] block top-1/2 -translate-y-1/2 text-dania-red-600 mt-3" aria-label="Toggle password visibility">
                    <SvgHelper height="16px" width="16px" :name="showPassword ? 'eye-off' : 'eye'" />
                  </button>
                </div>
                <div v-if="showErrorMsg" class="flex text-red-700 lg:text-p3-strong text-p3-strong-sm items-center justify-start flex-row gap-3" role="alert">
                  <SvgHelper name="alert-triangle" />{{ dictionary['Login.PasswordOrEmailIsIncorrect'] }}
                </div>
              </div>
              <div class="lg:mt-10 flex gap-3 lg:flex-row flex-col-reverse justify-center mt-8">
                <button type="button" @click="toggleForgotPassword" class="fill-button-light">{{ dictionary['Login.HaveYouForgotYourPassword'] }}</button>
                <button type="submit" class="fill-button-red" :disabled="!isFormFilled">{{ dictionary['Login.LogIn'] }}</button>
              </div>
            </template>

            <template v-else-if="showForgotPassword">
              <h2 class="lg:text-h1 text-h1-sm text-center mb-6">{{ dictionary['Login.ForgotPassword'] }}</h2>
              <span class="lg:text-p1 text-p1-sm text-center mb-3 flex">{{ dictionary['Login.IfYouHaveForgottenYourPasswordYouCanEnterEmail'] }}</span>
              <div class="flex flex-col gap-6 mt-7">
                <FormKit
                  name="emailreset"
                  label="E-mail"
                  type="email"
                  validation="required|email"
                  :validation-messages="{
                    required: 'Indtast en korrekt e-mail',
                  }"
                  placeholder="Indtast din e-mail"
                  input-class="text-dania-grey-600"
                  aria-required="true"
                  autocomplete="username"
                />
              </div>
              <div class="lg:mt-10 flex gap-3 lg:flex-row flex-col justify-center mt-8">
                <button type="button" class="fill-button-light" @click="toggleForgotPassword">{{ dictionary['Login.BackToLogIn'] }}</button>
                <button type="button" @click="handlePassReset" class="fill-button-red" :disabled="!isRequestEmail">{{ dictionary['Login.SendMail'] }}</button>
              </div>
            </template>
            <template v-else-if="isLoggedIn">
              <SvgHelper Class="text-dania-red-700 flex justify-center lg:mb-6" width="60px" height="60px" name="check-red" />
              <h2 class="lg:text-h1 text-h1-sm text-center mb-6">{{ dictionary['Login.AreYouSureYouWantToLogOut'] }}</h2>
              <span class="lg:text-p1 text-p1-sm text-center mb-3 flex">{{ dictionary['Login.WhenYouLogOut'] }} </span>
              <div class="lg:mt-10 flex gap-3 lg:flex-row flex-col justify-center mt-8">
                <button type="button" class="fill-button-light" @click="closeModal">{{ dictionary['Login.NoCancel'] }}</button>
                <button type="button" class="fill-button-red" @click="handleLogOut">{{ dictionary['Login.YesLogOut'] }}</button>
              </div>
            </template>
            <template v-else>
              <SvgHelper Class="text-dania-red-700 flex justify-center lg:mb-6" width="60px" height="60px" name="check-red" />
              <h2 class="lg:text-h1 text-h1-sm text-center mb-6">{{ dictionary['Login.MailSendt'] }}</h2>
              <span class="lg:text-p1 text-p1-sm text-center mb-3 flex">{{ dictionary['Login.IfYouHaveForgottenYourPasswordYouCanEnterEmail'] }}</span>
              <div class="lg:mt-10 flex gap-3 lg:flex-row flex-col justify-center mt-8">
                <button type="button" class="fill-button-light" @click="closeModal">{{ dictionary['Login.CloseThisWindow'] }}</button>
              </div>
            </template>
          </div>

          <div v-if="!isLoggedIn && IsModal" class="bg-dania-red-700 text-white flex justify-center gap-3 py-6 px-2 lg:flex-row flex-col">
            <p class="lg:text-p1 text-p1-sm">{{ dictionary['Login.AreYouNotAMemberOfRealdania'] }}</p>
            <a href="/bliv-klubmedlem" class="lg:text-t3 text-t3-sm underline">{{ dictionary['Login.BecomeClubMemberHere'] }}</a>
          </div>
        </section>
      </FormKit>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ref, onMounted, onBeforeUnmount, computed, getCurrentInstance } from 'vue';
import SvgHelper from '../helpers/svgHelper.vue';
import Spinner from '../modals/spinner.vue';
import { login, LoginRequest, LoginResponse } from '../../../scripts/api/logIn';
import { logOut } from '../../../scripts/api/logOut';
import { resetPassword } from '../../../scripts/api/forgotPassword';
import { Dictionary } from '../../interfaces/Globals';

const instance = getCurrentInstance();
const dictionary = ref<Dictionary>(window.dictionary);
const isLoggedIn = window.isLoggedIn;
const IsModal = ref(instance?.attrs.modal !== 'false');
const showPassword = ref(false);
const showErrorMsg = ref(false);
const isLoading = ref(false);
const showForgotPassword = ref(false);
const showDefault = ref(true);
const showMailSend = ref(false);
const loginContainer = ref(null);

const formData = ref({
  username: '',
  password: '',
  emailreset: '',
});

const isFormFilled = computed(() => {
  return formData.value.username !== '' && formData.value.password !== '';
});
const isRequestEmail = computed(() => {
  return formData.value.emailreset !== '';
});

const handleSubmit = async () => {
  const request: LoginRequest = {
    username: formData.value.username,
    password: formData.value.password,
  };

  try {
    isLoading.value = true;

    const response: LoginResponse = await login(request);
    window.location.reload();

    console.log('Login successful:', response);
  } catch (error) {
    showErrorMsg.value = true;
    console.error('Login failed:', error);
    isLoading.value = false;
  } finally {
    isLoading.value = false;
  }
};

const handlePassReset = async () => {
  const request = {
    email: formData.value.emailreset,
  };

  try {
    isLoading.value = true;
    const response = await resetPassword(request);
    console.log('Email send:', response);
  } catch (error) {
    console.error('Email failed:', error);
    isLoading.value = false;
  } finally {
    isLoading.value = false;
    showMailSend.value = true;
    showForgotPassword.value = false;
  }
};

const handleLogOut = async () => {
  try {
    isLoading.value = true;
    await logOut();
    window.location.href = '/';
  } catch (error) {
    console.error('Logout failed:', error);
  } finally {
    isLoading.value = false;
  }
};

const closeModal = () => {
  if (IsModal.value === false) {
    return;
  }
  const Cmodal = document.querySelector('[data-login]');
  showForgotPassword.value = false;
  showMailSend.value = false;
  showDefault.value = true;
  Cmodal.classList.add('hidden');
  Cmodal.setAttribute('aria-hidden', 'true');
};

const togglePasswordVisibility = () => {
  showPassword.value = !showPassword.value;
};

const toggleForgotPassword = () => {
  showForgotPassword.value = !showForgotPassword.value;
  showErrorMsg.value = false;
  showDefault.value = !showDefault.value;
};

const trapFocus = (event: KeyboardEvent) => {
  if (event.key !== 'Tab') return;

  const focusableElements = loginContainer.value.querySelectorAll('a, button, input, textarea, select, details, [tabindex]:not([tabindex="-1"])');
  const firstElement = focusableElements[0] as HTMLElement;
  const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement;

  if (event.shiftKey && document.activeElement === firstElement) {
    lastElement.focus();
    event.preventDefault();
  } else if (!event.shiftKey && document.activeElement === lastElement) {
    firstElement.focus();
    event.preventDefault();
  }
};

onMounted(() => {
  document.addEventListener('keydown', trapFocus);
});

onBeforeUnmount(() => {
  document.removeEventListener('keydown', trapFocus);
});
</script>

<style lang="scss">
#login-modal {
  input {
    @apply text-dania-grey-600 border border-dania-red-200 outline-none;
    &::placeholder {
      @apply text-dania-grey-600;
    }
    &:focus {
      @apply border bg-dania-red-50 border-dania-red-200 outline-none;
    }
  }

  .formkit-wrapper {
    .formkit-inner {
      @apply bg-white h-8 flex justify-center items-center w-full  rounded-lg border overflow-hidden;
      input {
        @apply w-full h-full px-3 rounded-lg;
      }
    }
    .formkit-label {
      @apply lg:text-p2-strong text-p2-strong;
    }
  }
}
</style>
